/**
 * OrderPhotoUploader Component
 *
 * Reusable photo uploader for orders (used in create and edit forms).
 * - Uploads each selected image to the public gallery storage endpoint
 * - Shows preview thumbnails with a remove button
 * - Enforces a max number of photos and image-only selection
 *
 * Note: the storage endpoint accepts one file per request and is rate-limited
 * (10 uploads/hour per user), so each image is uploaded as a separate request.
 */

'use client';

import { FC, useRef, useState } from 'react';
import { toast } from 'sonner';
import { buildStaticUrl } from '@/src/shared/api';
import { uploadGalleryImage } from '@/src/entities/user/api';

export interface OrderPhotoUploaderProps {
  images: string[];
  onChange: (images: string[]) => void;
  max?: number;
  disabled?: boolean;
}

export const OrderPhotoUploader: FC<OrderPhotoUploaderProps> = ({
  images,
  onChange,
  max = 10,
  disabled = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset input so the same file can be selected again after removal
    e.target.value = '';
    if (files.length === 0) return;

    const remainingSlots = max - images.length;
    if (remainingSlots <= 0) {
      toast.error(`You can upload up to ${max} photos`);
      return;
    }

    const toUpload = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more photo(s) can be added`);
    }

    setUploading(true);
    try {
      const results = await Promise.allSettled(
        toUpload.map(async (file) => {
          if (!file.type.startsWith('image/')) {
            throw new Error(`${file.name} is not an image`);
          }
          const { url } = await uploadGalleryImage(file);
          return url;
        })
      );

      const uploaded = results
        .filter((r): r is PromiseFulfilledResult<string> => r.status === 'fulfilled')
        .map((r) => r.value);
      const failedCount = results.length - uploaded.length;

      if (uploaded.length > 0) {
        onChange([...images, ...uploaded]);
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} photo(s) failed to upload`);
      }
    } catch (error) {
      console.error('[OrderPhotoUploader] Failed to upload photos:', error);
      toast.error('Failed to upload photos. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (url: string) => {
    onChange(images.filter((img) => img !== url));
  };

  const reachedMax = images.length >= max;

  return (
    <div>
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-3 sm:grid-cols-4">
          {images.map((url) => (
            <div
              key={url}
              className="relative aspect-square overflow-hidden rounded-lg border border-gray-200"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={buildStaticUrl(url)}
                alt="Order attachment"
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemove(url)}
                disabled={disabled}
                className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Remove photo"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleSelect}
        className="hidden"
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled || uploading || reachedMax}
        className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-blue-500 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {uploading ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
            Uploading...
          </>
        ) : (
          <>
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5V18a2.25 2.25 0 002.25 2.25h13.5A2.25 2.25 0 0021 18v-1.5m-13.5-6L12 4.5m0 0l4.5 4.5M12 4.5V15"
              />
            </svg>
            Upload Photos
          </>
        )}
      </button>
      <p className="mt-1 text-xs text-gray-500">
        {images.length}/{max} photos · JPG, PNG, WebP
      </p>
    </div>
  );
};
